import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, MapPinned } from "lucide-react";
import SEO from "../components/SEO";
import { STATS, SITE } from "../lib/site";

export default function About() {
  return (
    <>
      <SEO
        title="About Alliance Crane Service — DeLand, FL"
        description="Family-operated mobile crane company based in DeLand, Florida. NCCCO-certified operators, fully insured, focused on safety and on-time performance."
      />

      <section className="border-b border-[var(--ac-border)] bg-black">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-28">
          <div className="overline mb-4">About</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            A Central Florida crane outfit you can <span className="text-[var(--ac-red)]">actually reach.</span>
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="text-lg text-zinc-300 leading-relaxed">
              Alliance Crane Service LLC was built around a simple idea: when a contractor calls
              a crane company, they should get a person — not a phone tree — and a plan, not a
              push-back.
            </p>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Based in DeLand, we run two well-maintained hydraulic mobile cranes: a 40-ton
              Grove and a 50-ton Manitex. The right size for the work most Central Florida
              builders need every day — HVAC sets, truss drops, machinery moves, light steel and
              after-hours emergency picks.
            </p>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Every job runs through a pre-lift plan. Every operator carries current NCCCO
              certification. Every truck rolls out fully insured. That's the bar — we don't move
              it for anyone.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--ac-border)]">
              {[
                { i: ShieldCheck, t: "Safety First", b: "Pre-lift plans, signal training, ground assessments." },
                { i: Wrench, t: "Well-Maintained", b: "In-house inspections and regular outside service." },
                { i: MapPinned, t: "Local", b: "Family-operated. Based in Volusia County." },
              ].map((v) => (
                <div key={v.t} className="bg-[var(--ac-bg)] p-6">
                  <v.i className="w-6 h-6 text-[var(--ac-red)] mb-3" />
                  <div className="font-display text-xl mb-1">{v.t}</div>
                  <div className="text-sm text-zinc-400">{v.b}</div>
                </div>
              ))}
            </div>
          </div>

          <aside className="md:col-span-5 md:pl-6">
            <div className="surface p-7">
              <div className="overline mb-4">By The Numbers</div>
              <div className="grid grid-cols-2 gap-px bg-[var(--ac-border)]">
                {STATS.map((s) => (
                  <div key={s.label} className="bg-[var(--ac-bg-2)] p-5">
                    <div className="font-display text-3xl">{s.value}</div>
                    <div className="overline mt-1 !text-[10px]">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 space-y-2 text-sm text-zinc-300">
                <div className="overline mb-3">Operating Hours</div>
                <div className="flex justify-between border-b border-[var(--ac-border)] py-2">
                  <span>Monday – Friday</span>
                  <span className="font-mono-data">7:00 AM – 5:00 PM</span>
                </div>
                <div className="flex justify-between border-b border-[var(--ac-border)] py-2">
                  <span>Saturday</span>
                  <span className="font-mono-data">By Appointment</span>
                </div>
                <div className="flex justify-between py-2 text-[var(--ac-red)] font-semibold">
                  <span>Emergency Lifts</span>
                  <span className="font-mono-data">24 / 7</span>
                </div>
              </div>

              <Link to="/contact" className="btn-primary w-full justify-center mt-6" data-testid="about-cta">
                Start a Project <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
